// Real-time WhatsApp Business Calling — the first live-push consumer in the
// back-office. Mounted ONCE at the App root (not per-page) so the WebSocket
// connection survives navigation: a buyer's call can't be allowed to silently
// vanish just because the operator was looking at an order when it rang.
import { useState, useEffect, useRef, useCallback } from 'react';
import { getWhatsAppWsUrl, acceptWhatsAppCall, rejectWhatsAppCall, endWhatsAppCall, placeWhatsAppCall, requestWhatsAppCallPermission, NO_CALL_PERMISSION } from './backOfficeApi';
import { toast } from './buses';
import { createReconnectBackoff } from './reconnectBackoff';

export function useCallCenter() {
  const [connection, setConnection] = useState('connecting'); // connecting | connected | reconnecting | offline | gave-up
  const [ringingCalls, setRingingCalls] = useState([]);
  const [activeCall, setActiveCall] = useState(null);
  const [callStartedAt, setCallStartedAt] = useState(null);
  const [muted, setMuted] = useState(false);
  // Ticks whenever a call starts, ends or is declined — the call history (CallCenterPage, its one reader) re-reads
  // its first page on each tick. The history itself lives on that page, a page at a time.
  const [historyTick, setHistoryTick] = useState(0);

  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const streamRef = useRef(null);
  const audioElRef = useRef(null);
  const activeCallRef = useRef(null);
  useEffect(() => { activeCallRef.current = activeCall; }, [activeCall]);
  // An answer that lands before placeWhatsAppCall has returned its call id (a quick pick-up) waits here.
  const earlyAnswersRef = useRef(new Map());
  const cancelledDialRef = useRef(false);

  // Release the microphone and the peer connection — every way a call ends goes through here.
  const releaseMedia = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    pcRef.current?.close();
    pcRef.current = null;
    streamRef.current = null;
  };

  // They picked up a call we placed: Meta's answer completes the connection and the clock starts.
  const applyAnswer = async (sdp) => {
    try {
      await pcRef.current?.setRemoteDescription({ type: 'answer', sdp });
      setActiveCall((c) => (c ? { ...c, state: 'connected' } : c));
      setCallStartedAt(Date.now());
    } catch (e) {
      toast('The call could not connect', 'error', e?.message || 'WhatsApp sent an answer this browser could not use');
    }
  };
  // Lets `reconnect()` (below) reach the CURRENT effect's connect closure —
  // there's no live-call polling fallback like Chat has, so once the backoff
  // gives up an operator needs a real way back in short of reloading the page.
  const manualReconnectRef = useRef(() => {});

  const refreshHistory = useCallback(() => setHistoryTick((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    let retryTimer;
    const backoff = createReconnectBackoff();

    // Schedules the next attempt, or gives up for good once the backoff is
    // exhausted (a genuinely down backend, not a transient blip). There's no
    // passive fallback for a missed live call, so give-up is a distinct,
    // visible state — the operator gets a manual "Reconnect" control instead
    // of the app silently never trying again.
    const retryOrGiveUp = () => {
      const delay = backoff.next();
      if (delay == null) { setConnection('gave-up'); return; }
      retryTimer = setTimeout(connect, delay);
    };

    const connect = () => {
      setConnection((c) => (c === 'connected' ? 'reconnecting' : 'connecting'));
      let ws;
      try {
        ws = new WebSocket(getWhatsAppWsUrl());
      } catch {
        setConnection('offline');
        retryOrGiveUp();
        return;
      }
      wsRef.current = ws;
      ws.onopen = () => { if (!cancelled) { setConnection('connected'); backoff.reset(); } };
      ws.onclose = (e) => {
        if (cancelled) return;
        setConnection('offline');
        if (e.code !== 1000) retryOrGiveUp();
      };
      ws.onerror = () => { if (!cancelled) setConnection('offline'); };
      ws.onmessage = (event) => {
        let msg;
        try { msg = JSON.parse(event.data); } catch { return; }
        if (msg.type === 'incoming_call') {
          setRingingCalls((prev) => (prev.some((c) => c.callId === msg.data.callId) ? prev : [...prev, { ...msg.data, ringingSince: Date.now() }]));
          toast('Incoming WhatsApp call', 'info', msg.data.contactName || msg.data.from);
        } else if (msg.type === 'call_claimed') {
          // Another agent answered first — every OTHER dashboard's ringing
          // entry for this call is stale now. No toast: this fires on every
          // agent's socket including the one who just answered it themselves
          // (their own UI already reflects "on call"), so an intrusive alert
          // here would be noise for the winner and unnecessary drama for
          // bystanders — the ring silently disappearing is signal enough.
          const { callId } = msg.data || {};
          if (!callId) return;
          setRingingCalls((prev) => (prev.some((c) => c.callId === callId) ? prev.filter((c) => c.callId !== callId) : prev));
        } else if (msg.type === 'call_answered') {
          const { callId, sdpAnswer } = msg.data || {};
          if (!callId || !sdpAnswer) return;
          if (activeCallRef.current?.callId === callId) applyAnswer(sdpAnswer);
          else if (activeCallRef.current?.outbound && !activeCallRef.current.callId) earlyAnswersRef.current.set(callId, sdpAnswer);
        } else if (msg.type === 'call_ended') {
          // The far end hung up — either before anyone answered (clear the
          // ghost ring) or mid-conversation (stop the dead-air audio and free
          // the mic/peer connection; nobody clicked our own Hang up button).
          const { callId } = msg.data || {};
          if (!callId) return;
          setRingingCalls((prev) => (prev.some((c) => c.callId === callId) ? prev.filter((c) => c.callId !== callId) : prev));
          const current = activeCallRef.current;
          if (current?.callId === callId) {
            releaseMedia();
            setActiveCall(null);
            setCallStartedAt(null);
            const who = current.contactName || current.from;
            if (msg.data?.reason === 'declined') toast('Call declined', 'info', `${who} declined the call`);
            else if (current.outbound && current.state !== 'connected') toast('No answer', 'info', `${who} did not pick up`);
            else toast('Call ended', 'info', `${who} hung up`);
            refreshHistory();
          }
        }
      };
    };
    // Manual "Reconnect" (only meaningful after a give-up): drop any pending
    // retry, start the attempt count over, and try again right away.
    manualReconnectRef.current = () => { clearTimeout(retryTimer); backoff.reset(); connect(); };
    connect();
    return () => { cancelled = true; clearTimeout(retryTimer); wsRef.current?.close(1000); };
  }, []);

  const reconnect = useCallback(() => { manualReconnectRef.current(); }, []);
  const attachAudioEl = useCallback((el) => { audioElRef.current = el; }, []);

  const answerCall = useCallback(async (call) => {
    if (!call.sdpOffer) { toast('No SDP offer for this call', 'error', 'The webhook payload may have arrived incomplete'); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      streamRef.current = stream;
      const pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
      pcRef.current = pc;
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      pc.ontrack = (e) => { if (audioElRef.current) audioElRef.current.srcObject = e.streams[0]; };

      await pc.setRemoteDescription({ type: 'offer', sdp: call.sdpOffer });
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      if (pc.iceGatheringState !== 'complete') {
        await new Promise((resolve) => {
          const check = () => { if (pc.iceGatheringState === 'complete') { pc.removeEventListener('icegatheringstatechange', check); resolve(); } };
          pc.addEventListener('icegatheringstatechange', check);
          setTimeout(resolve, 3000);
        });
      }

      const browserSdp = pc.localDescription?.sdp;
      if (!browserSdp) throw new Error('Failed to get browser SDP');

      await acceptWhatsAppCall(call.callId, browserSdp);

      setActiveCall({ ...call, state: 'connected' });
      setCallStartedAt(Date.now());
      setMuted(false);
      setRingingCalls((prev) => prev.filter((c) => c.callId !== call.callId));
    } catch (e) {
      // The mic/peer connection may already be live at this point (a lost
      // race against another agent's Answer is now a real, expected way to
      // land here, not just a rare network blip) — release them, or the
      // browser's mic indicator stays on and the RTCPeerConnection leaks even
      // though this agent never actually got the call.
      releaseMedia();
      toast('Failed to answer call', 'error', e?.message || 'Microphone permission or network issue');
    }
  }, []);

  const declineCall = useCallback(async (call) => {
    try { await rejectWhatsAppCall(call.callId); } catch (e) { toast('Failed to decline', 'error', e?.message); }
    setRingingCalls((prev) => prev.filter((c) => c.callId !== call.callId));
    refreshHistory();
  }, [refreshHistory]);

  const hangUp = useCallback(async () => {
    if (!activeCall) return;
    // Still dialling (no call id yet): placeCall ends it on WhatsApp's side the moment the id arrives.
    if (!activeCall.callId) cancelledDialRef.current = true;
    else { try { await endWhatsAppCall(activeCall.callId); } catch (e) { toast('Failed to end call', 'error', e?.message); } }
    releaseMedia();
    setActiveCall(null);
    setCallStartedAt(null);
    refreshHistory();
  }, [activeCall, refreshHistory]);

  /**
   * CALL THEM — a WhatsApp call from the business number, from anywhere in the back office. The
   * browser makes the offer; WhatsApp rings them; their answer comes back over the socket
   * (call_answered). If they have not let this number call them, WhatsApp says so and the operator is
   * offered WhatsApp's own "may we call you?" prompt instead of a dead end.
   */
  const askToCall = useCallback(async (to) => {
    try { await requestWhatsAppCallPermission(to); toast('Asked to call', 'success', 'They get WhatsApp’s call-permission prompt; their answer appears in the chat.'); }
    catch (e) { toast('Could not ask', 'error', e?.message); }
  }, []);

  const placeCall = useCallback(async ({ to, contactName } = {}) => {
    if (activeCallRef.current) { toast('Already on a call', 'warning', 'End it before starting another.'); return; }
    cancelledDialRef.current = false;
    earlyAnswersRef.current.clear();
    setActiveCall({ callId: null, from: to, contactName: contactName || null, outbound: true, state: 'calling' });
    setCallStartedAt(null);
    setMuted(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      streamRef.current = stream;
      const pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
      pcRef.current = pc;
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      pc.ontrack = (e) => { if (audioElRef.current) audioElRef.current.srcObject = e.streams[0]; };
      await pc.setLocalDescription(await pc.createOffer());
      if (pc.iceGatheringState !== 'complete') {
        await new Promise((resolve) => {
          const check = () => { if (pc.iceGatheringState === 'complete') { pc.removeEventListener('icegatheringstatechange', check); resolve(); } };
          pc.addEventListener('icegatheringstatechange', check);
          setTimeout(resolve, 3000);
        });
      }
      if (cancelledDialRef.current) { releaseMedia(); return; }
      const { callId } = await placeWhatsAppCall(to, pc.localDescription.sdp, contactName);
      if (cancelledDialRef.current) { endWhatsAppCall(callId).catch(() => {}); return; }
      setActiveCall((c) => (c ? { ...c, callId, state: 'ringing' } : c));
      activeCallRef.current = { ...activeCallRef.current, callId, state: 'ringing' };
      const early = earlyAnswersRef.current.get(callId);
      if (early) applyAnswer(early);
      refreshHistory();
    } catch (e) {
      releaseMedia();
      setActiveCall(null);
      if (e?.code === NO_CALL_PERMISSION) {
        toast('They haven’t allowed calls', 'warning', 'WhatsApp needs their permission before this number can call them.', { label: 'Ask to call', onClick: () => askToCall(to) });
      } else {
        toast('Could not call', 'error', e?.name === 'NotAllowedError' ? 'The browser was not allowed to use the microphone.' : (e?.message || 'WhatsApp refused the call'));
      }
    }
  }, [askToCall, refreshHistory]);

  const toggleMute = useCallback(() => {
    const stream = streamRef.current;
    if (!stream) return;
    const next = !muted;
    stream.getAudioTracks().forEach((t) => { t.enabled = !next; });
    setMuted(next);
  }, [muted]);

  return {
    connection, ringingCalls, activeCall, callStartedAt, muted,
    historyTick, refreshHistory,
    answerCall, declineCall, hangUp, toggleMute, attachAudioEl, reconnect, placeCall, askToCall,
  };
}
